/**
 * La page — celle de n'importe quelle expérience.
 *
 * Ce module ne mentionne ni gnomon, ni Soleil, ni pesanteur. Il sait qu'une
 * expérience a une question, des réglages, éventuellement une vue, ce qu'elle
 * trace, ce qu'elle chiffre et ce qu'elle confronte ; il les dispose dans cet
 * ordre et branche les curseurs. Ajouter une expérience ne le touche pas.
 *
 * L'ordre des sections n'est pas décoratif. La question vient avant les
 * réglages, les réglages avant les prédictions, les prédictions avant la
 * confrontation, et les limites après tout le reste — c'est le trajet d'un
 * raisonnement, et l'inverser ferait lire des courbes avant d'avoir lu ce
 * qu'elles supposent.
 *
 * Le rendu se fait en deux temps : une coquille écrite une fois, puis les
 * seules régions qui dépendent de l'état sont réécrites. Ce n'est pas de
 * l'optimisation — réécrire tout le document arracherait le canvas WebGL d'une
 * vue 3D à chaque mouvement de curseur.
 */

import type { Experiment, ExperimentEntry } from "./types.js";
import { renderComparisonSection } from "../view/comparison-section.js";
import { renderContentProblems, renderContentSection, renderProtocol } from "../view/content.js";
import {
  bindControls,
  renderFreeHypothesisControls,
  renderSituationControls,
} from "../view/controls.js";
import { renderReadout } from "../view/readout.js";
import { renderSeriesChart } from "../view/series.js";
import { escape, escapeAttribute } from "../view/format.js";
import { fr } from "../i18n/fr.js";

function section(title: string, body: string, region?: string): string {
  const attribute = region === undefined ? "" : ` data-region="${escapeAttribute(region)}"`;
  return [
    `<section class="verlet-section"${attribute}>`,
    `<h4>${escape(title)}</h4>`,
    `<div data-body>${body}</div>`,
    `</section>`,
  ].join("");
}

/**
 * La coquille, exportée pour être testable.
 *
 * L'environnement de test n'a pas de DOM : le montage réel n'y est pas
 * observable. La composition de la page, elle, est une fonction pure de
 * l'expérience et de son état — et c'est là que se logent les défauts qui
 * comptent, comme une section de confrontation qu'une expérience aurait
 * discrètement omise.
 */
export function renderExperimentShell<S>(experiment: Experiment<S>, settings: S): string {
  const content = experiment.content;

  const opening = content.ok
    ? section(content.content.question.title, renderContentSection(content.content.question))
    : section(fr.contentInvalid, renderContentProblems(content.problems));

  const closing = content.ok
    ? [
        section(content.content.protocol.title, renderProtocol(content.content.protocol)),
        section(content.content.limits.title, renderContentSection(content.content.limits)),
        section(content.content.undecided.title, renderContentSection(content.content.undecided)),
      ]
    : [];

  const scene =
    experiment.scene === undefined
      ? ""
      : section(experiment.scene.title, experiment.scene.shell());

  const hypotheses = experiment.controls.filter((spec) => spec.freeHypothesis);

  return [
    `<header class="verlet-header">`,
    `<h3>${escape(experiment.question)}</h3>`,
    `<p class="verlet-lede">${escape(experiment.lede)}</p>`,
    `</header>`,
    opening,
    section(
      experiment.situationTitle,
      renderSituationControls(
        experiment.controls,
        experiment.presets,
        experiment.presetsLabel,
        experiment.situationCaveat,
        settings,
      ),
    ),
    scene,
    // Une expérience dont aucun modèle ne réclame d'hypothèse libre n'a pas de
    // section vide à montrer : elle n'a rien à concéder, et c'est une bonne
    // nouvelle, pas un trou dans la page.
    hypotheses.length === 0
      ? ""
      : section(
          experiment.hypothesisTitle,
          renderFreeHypothesisControls(
            experiment.controls,
            experiment.hypothesisCaveat,
            settings,
          ),
        ),
    section(fr.sectionPredictions, "", "predictions"),
    experiment.observations === undefined
      ? ""
      : section(
          experiment.observations.title,
          experiment.observations.render(settings),
          "observations",
        ),
    // Le graphe de comparaison et sa table vivent dans la même section : ce
    // sont deux rendus de la même chose, et les séparer faisait afficher le
    // même refus deux fois de suite, ce qui se lit comme une répétition
    // parasite plutôt que comme une contrainte.
    section(fr.sectionComparison, "", "comparison"),
    ...closing,
  ].join("");
}

function fill(root: ParentNode, region: string, html: string): void {
  const body = root.querySelector(`[data-region="${region}"] [data-body]`);
  if (body !== null) body.innerHTML = html;
}

function update<S>(root: ParentNode, experiment: Experiment<S>, settings: S): void {
  fill(
    root,
    "predictions",
    renderSeriesChart(experiment.chart(settings)) + renderReadout(experiment.readout(settings)),
  );
  if (experiment.observations !== undefined) {
    fill(root, "observations", experiment.observations.render(settings));
  }
  fill(root, "comparison", renderComparisonSection(experiment.comparison(settings)));
}

/**
 * Enferme une expérience et son état dans une entrée montable.
 *
 * L'état est une variable de fermeture : il ne quitte jamais ce cadre, et deux
 * expériences ne peuvent pas se marcher dessus. C'est aussi ce qui permet de
 * quitter une expérience et d'y revenir dans l'état où on l'a laissée — ce qui
 * est le minimum quand on vient de passer cinq minutes à régler des curseurs.
 */
export function experimentEntry<S>(experiment: Experiment<S>): ExperimentEntry {
  let settings = experiment.initial;

  return {
    id: experiment.id,
    label: experiment.label,
    question: experiment.question,
    // Dérivé, jamais déclaré : une analyse confronte si — et seulement si —
    // elle sait recevoir des mesures du dehors. Voir `ExperimentEntry`.
    confronts: experiment.observations !== undefined,
    mount(host: HTMLElement): () => void {
      host.innerHTML = renderExperimentShell(experiment, settings);
      update(host, experiment, settings);

      // Écrit par les contrôles comme par la vue : une scène interactive est
      // un moyen de régler parmi d'autres, et l'état reste unique.
      const apply = (next: S, redrawScene: boolean): void => {
        settings = next;
        update(host, experiment, settings);
        if (redrawScene) scene?.redraw();
      };

      // La vue se redessine elle-même après avoir écrit : lui demander de le
      // refaire la ferait travailler deux fois par clic.
      const scene = experiment.scene?.mount(
        host,
        () => settings,
        (next) => {
          apply(next, false);
        },
      );

      const detachObservations = experiment.observations?.mount(
        host,
        () => settings,
        (next) => {
          apply(next, true);
        },
      );

      bindControls(
        host,
        experiment.controls,
        experiment.presets,
        () => settings,
        (next) => {
          apply(next, true);
        },
      );

      return () => {
        detachObservations?.();
        scene?.detach();
        host.innerHTML = "";
      };
    },
  };
}
